import {workspaceAccent} from './app/colors';
import {Modal} from './ui';
import WorkspaceShell from './app/WorkspaceShell';
import Today from './app/Today';
import {confirmPendingChanges} from './hooks/useUnsavedChanges';
import {pageDefinitions,sidebarSections} from './app/navigation';
import React, { useEffect, useState, useRef } from 'react';
import Gates from './Gates';
import LoadingBays from './LoadingBays';
import Vehicles from './Vehicles';
import Drivers from './Drivers';
import VehicleClasses from './VehicleClasses';
import GeneralSettings from './GeneralSettings';
import WebsiteUsers from './WebsiteUsers';
import JobPlanner from './JobPlanner';
import Jobs from './Jobs';
import LicenseView from './LicenseView';
import GateDisplay from './GateDisplay';
import LayoutEditor from './LayoutEditor';
import LayoutViewer from './LayoutViewer';
import DisplayLayoutEditor from './DisplayLayoutEditor';
import { API_BASE, apiFetch, clearStoredUser, getStoredUser, showAppConfirm, storeUser } from './api';
import {
  applyThemeVariables,
  brandedPageStyle,
  mergeStyles,
  theme,
  THEME_MODE_DARK,
  THEME_MODE_LIGHT,
} from './theme';

const APP_NAME = 'DispoBoard';
const DEFAULT_FAVICON = `${process.env.PUBLIC_URL || ''}/favicon.ico`;
const THEME_STORAGE_PREFIX = 'dispoboard_theme_mode';
const SIDEBAR_STORAGE_PREFIX = 'dispoboard_sidebar_collapsed';

const MOBILE_RESTRICTED_PAGE_KEYS = new Set();

const styles = {
  displayLicenseInactive: {
    width: '100vw',
    height: '100vh',
    margin: 0,
    display: 'grid',
    placeContent: 'center',
    gap: '12px',
    background: '#000000',
    color: '#ffffff',
    fontFamily: theme.fontStack,
    textAlign: 'center',
    fontSize: 'clamp(22px, 4vw, 44px)',
    fontWeight: 800
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: 0
  },
  brandCollapsed: {
    justifyContent: 'center',
    gap: 0
  },
  brandLogo: {
    width: '48px',
    height: '48px',
    borderRadius: '14px',
    objectFit: 'cover',
    border: '1px solid var(--board-border, rgba(148,163,184,0.16))',
    background: 'var(--board-surface-soft, rgba(255,255,255,0.04))',
    flex: '0 0 auto'
  },
  brandFallback: {
    width: '48px',
    height: '48px',
    borderRadius: '14px',
    display: 'grid',
    placeItems: 'center',
    background: 'var(--board-accent-gradient-soft, linear-gradient(135deg, rgba(59,130,246,0.26) 0%, rgba(99,102,241,0.26) 100%))',
    border: '1px solid var(--board-accent-border, rgba(96,165,250,0.24))',
    color: 'var(--board-text-strong, #f8fafc)',
    fontWeight: 800,
    letterSpacing: '0.06em',
    flex: '0 0 auto'
  },
  brandCopy: {
    display: 'grid',
    gap: '2px',
    minWidth: 0
  },
  brandCopyCollapsed: {
    display: 'none'
  },
  brandTitle: {
    fontSize: '25px',
    lineHeight: 1,
    fontWeight: 750,
    letterSpacing: '-0.04em',
    color: 'var(--board-text-strong, #f8fafc)'
  },
  brandBoard: {
    fontSize: '11px',
    color: 'var(--board-text-faint, rgba(226,232,240,0.48))',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden'
  },
  deniedCard: {
    ...theme.glassCard,
    padding: '28px',
    color: 'var(--board-text-strong, #f8fafc)',
    fontSize: '20px',
    fontWeight: 800
  },
  mobileNoticeCard: {
    ...theme.glassCard,
    padding: '24px',
    display: 'grid',
    gap: '12px',
    color: 'var(--board-text-strong, #f8fafc)'
  },
  mobileNoticeTitle: {
    margin: 0,
    fontSize: '22px',
    fontWeight: 800
  },
  mobileNoticeText: {
    margin: 0,
    color: 'var(--board-text-soft, rgba(226,232,240,0.74))',
    lineHeight: 1.6
  },
  loginShell: {
    minHeight: '100vh',
    padding: '24px',
    display: 'grid',
    placeItems: 'center'
  },
  loginKicker: {
    ...theme.pageEyebrow
  },
  loginCard: {
    ...theme.glassCard,
    width: 'min(100%, 360px)',
    padding: '24px',
    color: 'var(--board-text-strong, #f8fafc)',
    display: 'grid',
    gap: '14px',
    alignContent: 'start'
  },
  loginInput: {
    ...theme.input,
    width: '100%'
  },
  loginButton: {
    ...theme.button,
    ...theme.primaryButton,
    width: '100%'
  },
  errorText: {
    color: '#fca5a5',
    fontSize: '13px',
    margin: 0
  },
  disabledPage: {
    ...theme.page,
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    padding: '24px',
    boxSizing: 'border-box'
  },
  disabledPanel: {
    ...theme.glassCard,
    maxWidth: '760px',
    padding: '42px',
    color: 'var(--board-text-strong, #f8fafc)',
    textAlign: 'center'
  },
  disabledTitle: {
    margin: 0,
    fontSize: '32px',
    lineHeight: 1.15
  },
  modalText: {
    margin: 0,
    color: 'var(--board-text-soft, rgba(226,232,240,0.78))',
    lineHeight: 1.55,
    whiteSpace: 'pre-wrap'
  },
  modalActions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    marginTop: '20px'
  },
  modalDangerButton: {
    ...theme.button,
    ...theme.dangerButton
  }
};

function appTitle(boardName) {
  return boardName ? `${APP_NAME} - ${boardName}` : APP_NAME;
}

function getPageFromHash() {
  const key = window.location.hash.replace(/^#\/?/, '');
  return pageDefinitions.some((page) => page.key === key) ? key : 'overview';
}

function updatePageHash(key) {
  const hash = `#${key}`;
  if (window.location.hash !== hash) {
    window.history.pushState(null, '', hash);
  }
}

function getThemeStorageKey(username) {
  return `${THEME_STORAGE_PREFIX}:${String(username || 'guest').trim().toLowerCase()}`;
}

function getStoredThemeModeByUsername(username) {
  const systemMode = window.location.pathname.startsWith('/display') || window.matchMedia?.('(prefers-color-scheme: dark)').matches ? THEME_MODE_DARK : THEME_MODE_LIGHT;
  if (!username) return systemMode;
  try {
    const stored = window.localStorage.getItem(getThemeStorageKey(username));
    return stored === THEME_MODE_LIGHT || stored === THEME_MODE_DARK ? stored : systemMode;
  } catch {
    return THEME_MODE_DARK;
  }
}

function getStoredThemeModeForUser(user) {
  return getStoredThemeModeByUsername(user?.username);
}

function storeThemeModeForUser(user, mode) {
  if (!user?.username) return;
  try {
    window.localStorage.setItem(
      getThemeStorageKey(user.username),
      mode === THEME_MODE_LIGHT ? THEME_MODE_LIGHT : THEME_MODE_DARK,
    );
  } catch {
    // Theme preference is non-critical.
  }
}

function getSidebarStorageKey(username) {
  return `${SIDEBAR_STORAGE_PREFIX}:${username}`;
}

function getStoredSidebarCollapsedByUsername(username) {
  if (!username) return false;
  try {
    return window.localStorage.getItem(getSidebarStorageKey(username)) === '1';
  } catch {
    return false;
  }
}

function getStoredSidebarCollapsedForUser(user) {
  return getStoredSidebarCollapsedByUsername(user?.username);
}

function storeSidebarCollapsedForUser(user, collapsed) {
  if (!user?.username) return;
  try {
    window.localStorage.setItem(getSidebarStorageKey(user.username), collapsed ? '1' : '0');
  } catch {
    // Sidebar preference is non-critical.
  }
}

function setDocumentBranding(logoUrl, boardName = '') {
  document.title = appTitle(boardName);

  const favicon = document.querySelector("link[rel='icon']");
  if (favicon) {
    favicon.href = logoUrl || DEFAULT_FAVICON;
  }

  const appleIcon = document.querySelector("link[rel='apple-touch-icon']");
  if (appleIcon && logoUrl) {
    appleIcon.href = logoUrl;
  }
}

function hasPagePermission(user, pageKey) {
  if (!user) return false;
  if (user.permissions?.adminUser) return true;
  if (pageKey === 'overview') return true;
  if (pageKey === 'jobs') return Boolean(user.permissions?.planner);
  if (pageKey === 'loadingBayPlanner') {
    return Boolean(user.permissions?.loadingBayPlanner || user.permissions?.planner);
  }
  if (pageKey === 'loadingBays') {
    return Boolean(user.permissions?.loadingBays || user.permissions?.gates);
  }
  return Boolean(user.permissions?.[pageKey]);
}

function isPageFeatureAvailable(settings, pageKey) {
  if (pageKey === 'drivers') return settings.drivers_enabled !== false;
  if (pageKey === 'vehicles') return settings.vehicles_enabled !== false;
  if (pageKey === 'vehicleClasses') return settings.vehicle_classes_enabled !== false;
  if (pageKey === 'gates') return settings.gates_enabled !== false;
  if (pageKey === 'loadingBays') return settings.loading_bays_enabled !== false;
  if (pageKey === 'planner') return settings.gates_enabled !== false && settings.gate_management_enabled !== false;
  if (pageKey === 'loadingBayPlanner') return settings.loading_bays_enabled !== false && settings.loading_bay_management_enabled !== false;
  if (pageKey === 'jobs') return settings.gates_enabled !== false && settings.gate_management_enabled !== false;
  if (pageKey === 'layoutEditor') return settings.warehouse_layout_enabled !== false;
  return true;
}

function isPageRestrictedOnMobile(pageKey, currentUser) {
  if (currentUser?.bootstrap && pageKey === 'websiteUsers') return false;
  return MOBILE_RESTRICTED_PAGE_KEYS.has(pageKey);
}

function BrandLockup({ logoUrl, boardName, collapsed = false }) {
  return (
    <div style={mergeStyles(styles.brand, collapsed && styles.brandCollapsed)} title={boardName || 'Board Instanz'}>
      {logoUrl ? (
        <img style={styles.brandLogo} src={logoUrl} alt="" />
      ) : (
        <div style={styles.brandFallback}>DB</div>
      )}
      <div style={mergeStyles(styles.brandCopy, collapsed && styles.brandCopyCollapsed)}>
        <span style={styles.brandTitle}>{APP_NAME}</span>
        <span style={styles.brandBoard}>{boardName || 'Board Instanz'}</span>
      </div>
    </div>
  );
}

function LoginScreen({ logoUrl, boardName, accent1, accent2, themeMode, whiteAccent, onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const requestLogin = async (payload) => {
    const response = await fetch(`${API_BASE}/website-users/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const loginError = new Error(data.error || 'Login fehlgeschlagen');
      loginError.code = data.code;
      loginError.canTakeover = data.canTakeover === true;
      throw loginError;
    }
    return data;
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');

    try {
      const user = await requestLogin({ username, password });
      if (!user.token) throw new Error('Kein Token erhalten');
      onLogin(user);
    } catch (err) {
      if (err?.code === 'ACCOUNT_IN_USE' && err?.canTakeover) {
        const confirmed = await showAppConfirm(
          err.message || 'Dieses Konto wird bereits an anderer Stelle verwendet.',
          {
            title: 'Konto bereits aktiv',
            confirmLabel: 'Andere Session trennen',
            cancelLabel: 'Abbrechen',
            danger: true,
          },
        );
        if (!confirmed) return;
        try {
          const user = await requestLogin({ username, password, takeover: true });
          if (!user.token) throw new Error('Kein Token erhalten');
          onLogin(user);
          return;
        } catch (takeoverError) {
          setError(takeoverError.message || 'Login fehlgeschlagen');
          return;
        }
      }
      setError(err.message || 'Login fehlgeschlagen');
    }
  };

  return (
    <div style={brandedPageStyle(themeMode, accent1, accent2, whiteAccent)}>
      <div className="ws-login" style={styles.loginShell}>
        <form className="ws-login-card" style={styles.loginCard} onSubmit={submit}>
          <div style={{ display: 'grid', gap: '10px', justifyItems: 'start' }}>
            <BrandLockup logoUrl={logoUrl} boardName={boardName} />
            <h1>Willkommen zurück</h1><p style={styles.loginKicker}>Melde dich in deinem Arbeitsbereich an.</p>
          </div>
          <input
            aria-label="Benutzername"
            id="username"
            name="username"
            style={styles.loginInput}
            placeholder="Username"
            type="text"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
          />
          <input
            aria-label="Passwort"
            id="password"
            name="password"
            style={styles.loginInput}
            placeholder="Passwort"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
          />
          <button style={styles.loginButton} type="submit">Einloggen</button>
          {error && <p style={styles.errorText}>{error}</p>}
        </form>
      </div>
    </div>
  );
}

function DisplayLicenseInactive() {
  return (
    <main style={styles.displayLicenseInactive}>
      <strong>DISPOBOARD.EU</strong>
      <span>Lizenz nicht aktiv</span>
    </main>
  );
}

function DisplayLoading() {
  return <main style={styles.displayLicenseInactive} aria-hidden="true" />;
}

function MobileUnavailablePanel({ page }) {
  return (
    <section style={styles.mobileNoticeCard}>
      <h2 style={styles.mobileNoticeTitle}>{page?.label || 'Seite nicht verfügbar'}</h2>
      <p style={styles.mobileNoticeText}>
        Diese Ansicht ist auf Handy und Tablet bewusst ausgeblendet. Nutze für Lagereditor und andere Drag-Workflows bitte die Desktop-Ansicht.
      </p>
    </section>
  );
}

function App() {
  const currentPath = window.location.pathname;
  const isDisplayPath = currentPath.startsWith('/display');
  const [page, setPage] = useState(() => getPageFromHash());
  const acceptedLocation=useRef(window.location.href);
  useEffect(()=>{acceptedLocation.current=window.location.href},[page]);
  const [settings, setSettings] = useState({
    board_name: '',
    site_logo_url: '',
    drivers_enabled: false,
    vehicles_enabled: false,
    vehicle_classes_enabled: false,
    gates_enabled: false,
    loading_bays_enabled: false,
    gate_management_enabled: false,
    loading_bay_management_enabled: false,
    warehouse_layout_enabled: true,
    warehouse_lookup_enabled: true,
    theme_accent_1: '',
    theme_accent_2: '',
    default_white_mode_accent: '',
    license_preset_name: 'CUSTOM',
    instance_disabled: false,
  });
  const [, setMenuOpen] = useState(false);
  const [, setUserMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => getStoredUser());
  const [themeMode, setThemeMode] = useState(() => getStoredThemeModeForUser(getStoredUser()));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => getStoredSidebarCollapsedForUser(getStoredUser()));
  const [sessionBlocked, setSessionBlocked] = useState(false);
  const [sessionBlockedMessage, setSessionBlockedMessage] = useState('Dieses Konto wird aktuell in einer anderen Session verwendet.');
  const [sessionSwitchBusy, setSessionSwitchBusy] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [modalState, setModalState] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    setDocumentBranding('', '');
    fetch(`${API_BASE}/public/settings`)
      .then((response) => response.json())
      .then((data) => {
        const nextSettings = {
          board_name: data.board_name || '',
          site_logo_url: data.site_logo_url || '',
          drivers_enabled: data.drivers_enabled !== false,
          vehicles_enabled: data.vehicles_enabled !== false,
          vehicle_classes_enabled: data.vehicle_classes_enabled !== false,
          gates_enabled: data.gates_enabled !== false,
          loading_bays_enabled: data.loading_bays_enabled !== false,
          gate_management_enabled: data.gate_management_enabled !== false,
          loading_bay_management_enabled: data.loading_bay_management_enabled !== false,
          warehouse_layout_enabled: data.warehouse_layout_enabled !== false,
          warehouse_lookup_enabled: data.warehouse_lookup_enabled !== false,
          theme_accent_1: data.theme_accent_1 || '',
          theme_accent_2: data.theme_accent_2 || '',
          default_white_mode_accent: data.default_white_mode_accent || '',
          license_preset_name: data.license_preset_name || 'CUSTOM',
          instance_disabled: data.instance_disabled === true,
        };
        setSettings(nextSettings);
        setDocumentBranding(nextSettings.site_logo_url, nextSettings.board_name);
        setSettingsLoaded(true);
      })
      .catch(() => {
        setDocumentBranding('', '');
        setSettings((currentSettings) => ({
          ...currentSettings,
          gates_enabled: false,
          loading_bays_enabled: false,
          gate_management_enabled: false,
          loading_bay_management_enabled: false,
        }));
        setSettingsLoaded(true);
      });
  }, []);

  useEffect(() => {
    applyThemeVariables(
      themeMode,
      settings.theme_accent_1,
      settings.theme_accent_2,
      settings.default_white_mode_accent,
    );
    if(!isDisplayPath){const colors=workspaceAccent(themeMode==='light'?(settings.default_white_mode_accent||settings.theme_accent_1):settings.theme_accent_1,themeMode);document.documentElement.style.setProperty('--workspace-accent',colors.accent);document.documentElement.style.setProperty('--workspace-on-accent',colors.onAccent);}
  }, [themeMode, settings.theme_accent_1, settings.theme_accent_2, settings.default_white_mode_accent,isDisplayPath]);

  useEffect(() => {
    const username = currentUser?.username;
    if (username) {
      setThemeMode(getStoredThemeModeByUsername(username));
      setSidebarCollapsed(getStoredSidebarCollapsedByUsername(username));
    } else {
      setSidebarCollapsed(false);
    }
  }, [currentUser?.username]);

  useEffect(() => {
    const onSettingsUpdated = (event) => {
      setSettings((currentSettings) => {
        const nextSettings = {
          ...currentSettings,
          board_name: event.detail?.board_name || currentSettings.board_name,
          site_logo_url: event.detail?.site_logo_url || '',
          theme_accent_1: event.detail?.theme_accent_1 || currentSettings.theme_accent_1,
          theme_accent_2: event.detail?.theme_accent_2 || currentSettings.theme_accent_2,
          default_white_mode_accent: event.detail?.default_white_mode_accent || currentSettings.default_white_mode_accent,
          license_preset_name: event.detail?.license_preset_name || currentSettings.license_preset_name,
        };
        setDocumentBranding(nextSettings.site_logo_url, nextSettings.board_name);
        return nextSettings;
      });
    };

    window.addEventListener('dispoboard:settings-updated', onSettingsUpdated);
    return () => window.removeEventListener('dispoboard:settings-updated', onSettingsUpdated);
  }, []);

  useEffect(() => {
    const onUnauthorized = () => {
      clearStoredUser();
      setCurrentUser(null);
      setSessionBlocked(false);
      setUserMenuOpen(false);
      setMenuOpen(false);
      setPage('overview');
      updatePageHash('overview');
    };

    window.addEventListener('torverwaltung:unauthorized', onUnauthorized);
    return () => window.removeEventListener('torverwaltung:unauthorized', onUnauthorized);
  }, []);

  useEffect(() => {
    const onSessionConflict = (event) => {
      const nextMessage = event.detail?.message || 'Dieses Konto wird aktuell in einer anderen Session verwendet.';
      setSessionBlocked(true);
      setSessionBlockedMessage(nextMessage);
      setUserMenuOpen(false);
      setMenuOpen(false);
      setModalState({
        type: 'session-blocked',
        title: 'Session vorübergehend gesperrt',
        message: nextMessage,
        confirmLabel: 'Andere Session übernehmen',
        cancelLabel: 'Abbrechen',
        danger: true,
      });
    };

    window.addEventListener('torverwaltung:session-conflict', onSessionConflict);
    return () => window.removeEventListener('torverwaltung:session-conflict', onSessionConflict);
  }, []);

  useEffect(() => {
    const onModal = (event) => {
      setModalState(event.detail || null);
    };

    window.addEventListener('dispoboard:modal', onModal);
    return () => window.removeEventListener('dispoboard:modal', onModal);
  }, []);

  useEffect(() => {
    const onHashChange = async event => {if(event.state?.workspaceConfirmed||await confirmPendingChanges()){acceptedLocation.current=window.location.href;setPage(getPageFromHash());window.dispatchEvent(new Event('dispoboard:planning-focus'));}else window.history.pushState(null,'',acceptedLocation.current);};
    window.addEventListener('hashchange', onHashChange);
    window.addEventListener('popstate', onHashChange);
    return () => {window.removeEventListener('hashchange', onHashChange);window.removeEventListener('popstate', onHashChange);};
  }, []);

  useEffect(() => {
    if (!currentUser?.token || sessionBlocked) return undefined;

    let stopped = false;
    const sendHeartbeat = async () => {
      if (stopped) return;
      try {
        await apiFetch(`${API_BASE}/website-users/session/heartbeat`, { method: 'POST' });
      } catch {
        // apiFetch handles unauthorized/session-conflict state globally.
      }
    };

    sendHeartbeat();
    const intervalId = window.setInterval(sendHeartbeat, 30000);
    const onFocus = () => sendHeartbeat();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') sendHeartbeat();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stopped = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [currentUser?.token, sessionBlocked]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Individual displays own loading, license errors and freshness through their snapshot API.
  if (isDisplayPath && currentPath !== '/display/overview') return <GateDisplay />;

  if (isDisplayPath && !settingsLoaded) {
    return <DisplayLoading />;
  }

  if (currentPath === '/display/overview' && (settings.warehouse_layout_enabled === false || !((settings.gates_enabled !== false && settings.gate_management_enabled !== false) || (settings.loading_bays_enabled !== false && settings.loading_bay_management_enabled !== false)))) {
    return <DisplayLicenseInactive />;
  }

  if (currentPath === '/display/overview') {
    return <LayoutViewer fullScreen />;
  }

  const navigate = async (key) => {
    if (!await confirmPendingChanges()) return false;
    if (currentUser?.bootstrap && key !== 'websiteUsers') {
      setModalState({
        type: 'alert',
        title: 'Admin-User erforderlich',
        message: 'Du musst zuerst einen Admin-User anlegen, bevor weitere Daten sichtbar oder bearbeitbar sind.',
        confirmLabel: 'Verstanden',
      });
      return;
    }

    setPage(key);
    updatePageHash(key);
    acceptedLocation.current=window.location.href;
    setMenuOpen(false);
    setUserMenuOpen(false);
  };

  const handleLogin = (user) => {
    const normalizedUser = {
      ...user,
      permissions: user.permissions || { overview: true },
    };
    storeUser(normalizedUser);
    setThemeMode(getStoredThemeModeForUser(normalizedUser));
    setSessionBlocked(false);
    setCurrentUser(normalizedUser);
    const nextPage = normalizedUser.bootstrap ? 'websiteUsers' : getPageFromHash();
    setPage(nextPage);
    updatePageHash(nextPage);
  };

  const changeThemeMode = (nextMode) => {
    const normalizedMode = nextMode === THEME_MODE_LIGHT ? THEME_MODE_LIGHT : THEME_MODE_DARK;
    setThemeMode(normalizedMode);
    storeThemeModeForUser(currentUser, normalizedMode);
    setUserMenuOpen(false);
  };

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((value) => {
      const nextValue = !value;
      storeSidebarCollapsedForUser(currentUser, nextValue);
      return nextValue;
    });
  };

  const logout = async () => {
    if (!await confirmPendingChanges()) return;
    try {
      await apiFetch(`${API_BASE}/website-users/logout`, { method: 'POST' });
    } catch {
      // Local logout must still complete if the server session is already gone.
    } finally {
      clearStoredUser();
      setCurrentUser(null);
      setSessionBlocked(false);
      setUserMenuOpen(false);
      setMenuOpen(false);
      setPage('overview');
      updatePageHash('overview');
    }
  };

  const closeModal = (result = false) => {
    if (typeof modalState?.resolve === 'function') {
      modalState.resolve(result);
    }
    setModalState(null);
  };

  const reclaimBlockedSession = async () => {
    if (!currentUser?.token || sessionSwitchBusy) return;
    setSessionSwitchBusy(true);
    try {
      const response = await fetch(`${API_BASE}/website-users/session/reclaim`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${currentUser.token}`,
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const reclaimError = new Error(data.error || 'Session konnte nicht übernommen werden.');
        reclaimError.code = data.code;
        reclaimError.retryAfterSeconds = data.retryAfterSeconds;
        throw reclaimError;
      }
      setSessionBlocked(false);
      setSessionBlockedMessage('Dieses Konto wird aktuell in einer anderen Session verwendet.');
      setModalState(null);
    } catch (err) {
      if (err?.code === 'SESSION_SWITCH_RATE_LIMIT') {
        const nextMessage = err.message || 'Session-Wechsel ist aktuell noch gesperrt.';
        setSessionBlockedMessage(nextMessage);
        setModalState({
          type: 'session-blocked',
          title: 'Session-Wechsel begrenzt',
          message: nextMessage,
          confirmLabel: 'Andere Session übernehmen',
          cancelLabel: 'Abbrechen',
          danger: true,
        });
        return;
      }
      if (err?.message === 'Nicht eingeloggt') {
        clearStoredUser();
        setCurrentUser(null);
        setSessionBlocked(false);
        setModalState(null);
        return;
      }
      setModalState({
        type: 'alert',
        title: 'Übernahme fehlgeschlagen',
        message: err.message || 'Session konnte nicht übernommen werden.',
        confirmLabel: 'Verstanden',
        danger: true,
      });
    } finally {
      setSessionSwitchBusy(false);
    }
  };

  const modalOverlay = modalState && (
    <Modal title={modalState.title || 'Hinweis'} onClose={() => closeModal(false)}>
        <p style={styles.modalText}>{modalState.message || ''}</p>
        <div style={styles.modalActions}>
          {(modalState.type === 'confirm' || modalState.type === 'session-blocked') && (
            <button type="button" style={theme.button} onClick={() => closeModal(false)}>
              {modalState.cancelLabel || 'Abbrechen'}
            </button>
          )}
          {modalState.type === 'session-blocked' ? (
            <button
              type="button"
              style={styles.modalDangerButton}
              onClick={reclaimBlockedSession}
              disabled={sessionSwitchBusy}
            >
              {sessionSwitchBusy ? 'Übernehme...' : (modalState.confirmLabel || 'Übernehmen')}
            </button>
          ) : (
          <button
            type="button"
            style={modalState.danger ? styles.modalDangerButton : mergeStyles(theme.button, theme.primaryButton)}
            onClick={() => closeModal(true)}
          >
            {modalState.confirmLabel || 'OK'}
          </button>
          )}
        </div>
    </Modal>
  );

  const currentPageMeta = pageDefinitions.find((item) => item.key === page) || pageDefinitions[0];
  const isMobileShell = viewportWidth < 1180;

  const visibleSidebarSections = sidebarSections
    .map((section) => ({
      ...section,
      pages: section.pages
        .map((pageKey) => pageDefinitions.find((item) => item.key === (pageKey==='planner'&&(!hasPagePermission(currentUser,'planner')||!isPageFeatureAvailable(settings,'planner'))&&hasPagePermission(currentUser,'loadingBayPlanner')?'loadingBayPlanner':pageKey)))
        .filter((item) => item && (
          currentUser?.bootstrap
            ? item.key === 'websiteUsers'
            : hasPagePermission(currentUser, item.key)
              && isPageFeatureAvailable(settings, item.key)
              && (!isMobileShell || !isPageRestrictedOnMobile(item.key, currentUser))
        )),
    }))
    .filter((section) => section.pages.length > 0);

  const mobilePageRestricted = false;

  const canViewPage = currentUser?.bootstrap
    ? page === 'websiteUsers'
    : (hasPagePermission(currentUser, page) && isPageFeatureAvailable(settings, page));

  const renderPage = () => {
    if (!canViewPage) {
      return <div style={styles.deniedCard}>Fehlende Berechtigung</div>;
    }

    if (mobilePageRestricted) {
      return <MobileUnavailablePanel page={currentPageMeta} />;
    }

    switch (page) {
      case 'overview':
        return <Today canPlan={hasPagePermission(currentUser,'planner') && isPageFeatureAvailable(settings,'planner')} />;
      case 'planner':
      case 'loadingBayPlanner':
        return <JobPlanner />;
      case 'jobs':
        return <Jobs />;
      case 'license':
        return <LicenseView />;
      case 'gates':
        return <Gates />;
      case 'loadingBays':
        return <LoadingBays />;
      case 'vehicles':
        return <Vehicles />;
      case 'drivers':
        return <Drivers />;
      case 'vehicleClasses':
        return <VehicleClasses />;
      case 'layoutEditor':
        return <LayoutEditor />;
      case 'displayLayout':
        return <DisplayLayoutEditor />;
      case 'general':
        return <GeneralSettings />;
      case 'websiteUsers':
        return <WebsiteUsers currentUser={currentUser} />;
      default:
        return <div style={styles.deniedCard}>Seite nicht gefunden</div>;
    }
  };

  const showHelp = () => {
    setModalState({
      type: 'alert',
      title: 'Hilfe',
      message: 'Für Rückfragen zu Board, Berechtigungen oder Setup nutze die Management-Instanz oder den internen Support-Kanal.',
      confirmLabel: 'Schließen',
    });
  };

  if (!currentUser) {
    return (
      <>
        <LoginScreen
          logoUrl={settings.site_logo_url}
          boardName={settings.board_name}
          accent1={settings.theme_accent_1}
          accent2={settings.theme_accent_2}
          themeMode={themeMode}
          whiteAccent={settings.default_white_mode_accent}
          onLogin={handleLogin}
        />
        {modalOverlay}
      </>
    );
  }

  if (currentUser.instanceDisabled || settings.instance_disabled) {
    return (
      <>
        <div style={brandedPageStyle(themeMode, settings.theme_accent_1, settings.theme_accent_2, settings.default_white_mode_accent)}>
          <main style={styles.disabledPage}>
            <section style={styles.disabledPanel}>
              <h1 style={styles.disabledTitle}>Ihr Zugang zum DispoBoard ist aktuell deaktiviert</h1>
            </section>
          </main>
        </div>
        {modalOverlay}
      </>
    );
  }

  return <WorkspaceShell sections={visibleSidebarSections} page={page} onNavigate={navigate} boardName={settings.board_name} logoUrl={settings.site_logo_url} user={currentUser} themeMode={themeMode} onTheme={changeThemeMode} onLogout={logout} onHelp={showHelp} collapsed={sidebarCollapsed} onCollapse={toggleSidebarCollapsed}>
    {sessionBlocked ? <section className="ui-surface"><h2>Session temporär gesperrt</h2><p>{sessionBlockedMessage}</p><button onClick={reclaimBlockedSession} disabled={sessionSwitchBusy}>Andere Session wieder übernehmen</button></section> : renderPage()}
    {modalOverlay}
  </WorkspaceShell>;
}
export default App;
